import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { FileText, Download, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { ZapFormService } from '../../../../../core/services/zap-form.service';
import { BASE_URL_UI } from '../../../../../config/config';

/**
 * Form responses in My Zaps > Interactions.
 *
 * Renders nothing at all when the creator has never built a form — an empty
 * card explaining a feature they are not using would just be noise above the
 * Zap Link analytics they came here for.
 */

interface ZapFormResponsesProps {
  influencerId: number | string;
}

interface ZapFormRow {
  zap_form_id: number;
  slug: string;
  title: string;
  view_count: number;
  submission_count: number;
}

interface SubmissionRow {
  submission_id: number;
  zap_form_id: number;
  answers: any;
  field_labels: any;
  submitted_at: string;
}

const parseJson = (value: any) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return {}; }
};

const formatWhen = (value: string): string => {
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const hours24 = d.getHours();
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const minutes = `${d.getMinutes()}`.padStart(2, '0');
  return `${d.getDate()} ${months[d.getMonth()]}, ${hours12}:${minutes} ${hours24 < 12 ? 'AM' : 'PM'}`;
};

export const ZapFormResponses: React.FC<ZapFormResponsesProps> = ({ influencerId }) => {
  const [forms, setForms] = useState<ZapFormRow[]>([]);
  const [activeFormId, setActiveFormId] = useState<number | null>(null);
  const [submissions, setSubmissions] = useState<SubmissionRow[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!influencerId) return;
    ZapFormService.getMyForms(influencerId)
      .then((res: any) => {
        const rows: ZapFormRow[] = res?.data?.forms || [];
        setForms(rows);
        if (rows.length) setActiveFormId(rows[0].zap_form_id);
      })
      .catch(() => setForms([]))
      .finally(() => setLoading(false));
  }, [influencerId]);

  const loadSubmissions = useCallback(() => {
    if (!influencerId || !activeFormId) return;
    setLoadingRows(true);
    ZapFormService.getSubmissions({ influencer_id: influencerId, zap_form_id: activeFormId, limit: 50 })
      .then((res: any) => setSubmissions(res?.data?.submissions || []))
      .catch(() => setSubmissions([]))
      .finally(() => setLoadingRows(false));
  }, [influencerId, activeFormId]);

  useEffect(() => { loadSubmissions(); }, [loadSubmissions]);

  const activeForm = useMemo(
    () => forms.find((f) => f.zap_form_id === activeFormId) || null,
    [forms, activeFormId]
  );

  const handleExport = async () => {
    if (!activeFormId || exporting) return;
    setExporting(true);
    try {
      const res: any = await ZapFormService.exportSubmissions(influencerId, activeFormId);
      if (res?.status !== 1 || !res?.data?.csv) return;
      // A Blob rather than a data: URI — a long CSV blows past the URL length
      // limit in some browsers, and this also gives the file a real name.
      const blob = new Blob([res.data.csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.data.filename || 'zap-form-responses.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  // Never built a form — say nothing rather than advertise an empty state.
  if (loading || !forms.length) return null;

  return (
    <Box sx={{ border: '1px solid #ececf0', borderRadius: '14px', bgcolor: '#fff', p: { xs: 2, md: 2.5 }, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '10px', bgcolor: '#f5f3ff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <FileText className="h-4 w-4" style={{ color: '#7c3aed' }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: '0.95rem', color: '#111' }}>Form responses</p>
          <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6b7280' }}>
            People who filled in your form
          </p>
        </Box>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting || !submissions.length}
          style={{
            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
            border: '1px solid #ececf0', background: '#fff', borderRadius: 999,
            padding: '6px 12px', fontSize: 12, fontWeight: 600,
            color: submissions.length ? '#111' : '#9ca3af',
            cursor: submissions.length ? 'pointer' : 'default',
          }}
        >
          <Download className="h-3 w-3" />
          {exporting ? 'Preparing…' : 'CSV'}
        </button>
      </Box>

      {/* One tab per form, only when there is more than one to choose between. */}
      {forms.length > 1 && (
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
          {forms.map((form) => {
            const active = form.zap_form_id === activeFormId;
            return (
              <button
                key={form.zap_form_id}
                type="button"
                onClick={() => { setActiveFormId(form.zap_form_id); setExpanded(null); }}
                style={{
                  flexShrink: 0, borderRadius: 999, border: 'none', padding: '6px 12px',
                  fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                  background: active ? '#7c3aed' : '#f5f3ff', color: active ? '#fff' : '#7c3aed',
                  cursor: 'pointer',
                }}
              >
                {form.title || 'Untitled form'}
              </button>
            );
          })}
        </Box>
      )}

      {!!activeForm && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
          <p style={{ margin: 0, fontSize: '0.8125rem', color: '#374151' }}>
            <strong>{activeForm.submission_count}</strong> responses
          </p>
          <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6b7280' }}>
            {activeForm.view_count} views
          </p>
          <a
            href={`${BASE_URL_UI}/form/${activeForm.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#7c3aed' }}
          >
            <ExternalLink className="h-3 w-3" /> View form
          </a>
        </Box>
      )}

      {loadingRows ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={20} sx={{ color: '#7c3aed' }} />
        </Box>
      ) : !submissions.length ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>No responses yet</p>
          <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: '#9ca3af' }}>
            They will appear here as soon as someone fills in your form
          </p>
        </Box>
      ) : (
        <Box>
          {submissions.map((row) => {
            const answers = parseJson(row.answers);
            const labels = parseJson(row.field_labels);
            const keys = Object.keys(answers);
            const isOpen = expanded === row.submission_id;
            // Collapsed, a row shows the first two answers — usually name and
            // email, which is all it takes to recognise someone.
            const preview = keys.slice(0, 2).map((k) => answers[k]).filter(Boolean).join(' · ');

            return (
              <Box
                key={row.submission_id}
                sx={{ borderTop: '1px solid #f3f4f6', py: 1.25, cursor: 'pointer' }}
                onClick={() => setExpanded(isOpen ? null : row.submission_id)}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <p style={{
                      margin: 0, fontSize: '0.875rem', color: '#111',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isOpen ? 'normal' : 'nowrap',
                    }}>
                      {preview || 'Response'}
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>
                      {formatWhen(row.submitted_at)}
                    </p>
                  </Box>
                  {isOpen
                    ? <ChevronDown className="h-3.5 w-3.5" style={{ color: '#9ca3af', marginTop: 4 }} />
                    : <ChevronRight className="h-3.5 w-3.5" style={{ color: '#9ca3af', marginTop: 4 }} />}
                </Box>

                {isOpen && (
                  <Box sx={{ mt: 1, pl: 0.5 }}>
                    {keys.map((key) => (
                      <Box key={key} sx={{ mb: 0.75 }}>
                        {/* The label stored WITH the submission, not the form's
                            current one — a renamed question must not relabel
                            answers people already gave. */}
                        <p style={{ margin: 0, fontSize: '0.6875rem', color: '#9ca3af' }}>{labels[key] || key}</p>
                        <p style={{ margin: 0, fontSize: '0.8125rem', color: '#374151', wordBreak: 'break-word' }}>
                          {String(answers[key])}
                        </p>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ZapFormResponses;
